'use client';

import { useModals } from '@/contexts/ModalContext';
import { useTranslations } from 'next-intl';
import { IconAlertCircle } from '@tabler/icons-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCallback } from 'react';
import { useTelegramUsersDelete } from '@/generated';
import { useLogout } from '@/contexts/LoginContext';

type Props = {
  onOk?: () => void;
};

export const BeforeDeletingAccountModal: React.FC<Props> = ({ onOk }) => {
  const modals = useModals();
  const t = useTranslations('modals');
  const logout = useLogout();

  const { mutateAsync: deleteAcc } = useTelegramUsersDelete();

  const onClose = useCallback(() => {
    onOk?.();
    modals.close('before-deleting-account');
  }, [modals, onOk]);

  const onClickConfirmDeleting = useCallback(async () => {
    await deleteAcc();
    logout();
    onClose();
    document.location.href = '/';
  }, [logout, onClose, deleteAcc]);

  return (
    <AlertDialog open onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <div className='flex flex-col items-center text-center gap-[16px]'>
          <AlertDialogHeader className='items-center text-center'>
            <AlertDialogTitle>{t('beforeDeletingAccount.header')}</AlertDialogTitle>
          </AlertDialogHeader>

          <div className='flex items-center justify-center size-[64px] shrink-0 rounded-full bg-primary-tint/10'>
            <IconAlertCircle className='size-[36px] text-red-day' strokeWidth={1.75} />
          </div>

          <AlertDialogDescription asChild>
            <div className='flex flex-col gap-[8px] text-s-h text-text-primary-day'>
              {t('beforeDeletingAccount.sub_header')
                .split('\n\n')
                .map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
            </div>
          </AlertDialogDescription>
        </div>

        <AlertDialogFooter className='flex-col sm:flex-col sm:justify-start gap-[10px] w-full'>
          <AlertDialogCancel
            variant='default'
            onClick={onClose}
            className='h-[48px] w-full rounded-[16px] text-bg-day text-[16px] font-medium'
          >
            {t('beforeDeletingAccount.do_nothing')}
          </AlertDialogCancel>

          <AlertDialogAction
            variant='outline'
            onClick={onClickConfirmDeleting}
            className='h-[48px] w-full rounded-[16px] bg-transparent hover:bg-transparent border-primary-green-day text-text-primary-day text-[16px] font-medium'
          >
            {t('beforeDeletingAccount.confirm_deleting')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
